import React, { useState, useEffect, useRef } from "react"
import {
  Container,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import { Card } from "primereact/card"
import { useForm, Controller } from "react-hook-form"
import { Steps } from "primereact/steps"

import { InputText } from "primereact/inputtext"
import { Dropdown } from "primereact/dropdown"
import { MultiSelect } from "primereact/multiselect"
import { InputTextarea } from "primereact/inputtextarea"
import { Checkbox } from "primereact/checkbox"
import { ProgressSpinner } from "primereact/progressspinner"

import Swal from "sweetalert2"
import axios from "axios"
import { useNavigate } from "react-router-dom"
import { InputNumber } from "primereact/inputnumber"
import { Calendar } from "primereact/calendar"
import { TreeSelect } from "primereact/treeselect"

// Utility functions (same as before)
const transformCountries = countries =>
  countries.map(country => ({
    name: country.name,
    country_id: country.country_id,
  }))
const transformStates = states =>
  states.map(state => ({ name: state.name, state_id: state.state_id }))
const transformCities = cities =>
  cities.map(city => ({ name: city.name, city_id: city.city_id }))
const transformPrimarySkills = skills =>
  skills.map(skill => ({ name: skill.skill_name, id: skill.skill_id }))
const transformResumeSources = sources =>
  sources.map(source => ({
    label: source.resume_source,
    value: source.source_id,
  }))
const transformAddressLabels = labels =>
  labels.map(label => ({ label: label.address_label, value: label.id }))

const CandidateStepperForm = () => {
  document.title = "ATS - Add Candidate";
  const navigate = useNavigate()
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    trigger,
    control,
    reset,
    clearErrors
  } = useForm({
    defaultValues: {
      firstname: "",
      lastname: "",
      personal_email: "",
      phone: "",
      jobtitle: "", 
      Company: "", 
      availabilityDate: "", 
      relocation: false,
      private: false,
      source: null,
      primarySkills: [],
      secondarySkills: [], 
      otherSkills: [], 
      street_address: "", 
      street_address_line_2: "", 
      label: null, 
      userid: "", 
      current_salary: 0.0, 
      expected_salary: 0.0, 
    },
  })
   const [options, setOptions] = useState([]);

  useEffect(() => {
    
    const fetchReferredBy = async () => {
      try {
        const response = await axios.get(
          "/api/v1/get-all-referred-by/"
        );
        const mappedOptions = response.data.results.map((item) => ({
          label: item.value,
          value: item.id 
        }));
        setOptions(mappedOptions);
      } catch (error) {
        console.error("Failed to fetch referred by options:", error);
      }
    };

    fetchReferredBy();
  }, []);

const handleTabChange = async (index) => {
  let isValid = false;

  if (activeIndex === 0) {
    isValid = await trigger(["firstname", "personal_email", "phone"]);
  } else if (activeIndex === 1) {
    isValid = await trigger(["source", "primarySkills"]);
  } else if (activeIndex === 2) {
    isValid = await trigger(["resumeFile"]);
  } else if (activeIndex === 3) {
    isValid = await trigger(["street_address", "pincode"]);
  }

  if (isValid) {
    setActiveIndex(index);
  } else {
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Please fill in all mandatory fields before proceeding.',
      confirmButtonText: 'OK'
    });
  }
};

  const [activeIndex, setActiveIndex] = useState(0)
  const [countries, setCountries] = useState([])
  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  const [primarySkills, setPrimarySkills] = useState([])
  const [resumeSources, setResumeSources] = useState([])
  const [addressLabels, setAddressLabels] = useState([])
  const [loading, setLoading] = useState(false)
  const [isDuplicateEmail, setIsDuplicateEmail] = useState(false)
  const [duplicateCandidateId, setDuplicateCandidateId] = useState(null)
  const emailDebounceRef = useRef(null)

  const userInfos = JSON.parse(sessionStorage.getItem("atsuser-info"));
  const intialcompanyId = userInfos.user_details.companies_data[0].company_id;
  const [companyId, setCompanyId] = useState(intialcompanyId);

  const steps = [
    { label: "Basic Details" },
    { label: "Technical Skills" },
    { label: "Import Resume" },
    { label: "Contact Details" },
    { label: "Other Details" },
  ]
  const [candidateError, setCandidateError] = useState("")

  // Fetch all required data on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const [
          countriesResponse,
          skillsResponse,
          sourcesResponse,
          labelsResponse,
          companiesResponse,
        ] = await Promise.all([
          axios.get("/api/v1/get-all-countries/"),
          axios.get("/api/v1/get-all-skills/"),
          axios.get("/api/v1/resume-source/"),
          axios.get("/api/v1/get-all-address-label/"),
          axios.get("/api/v1/get-all-companies/"),
        ])

        setCountries(transformCountries(countriesResponse.data.results))
        setPrimarySkills(transformPrimarySkills(skillsResponse.data.results))
        setResumeSources(transformResumeSources(sourcesResponse.data.results))
        setAddressLabels(transformAddressLabels(labelsResponse.data.results))
        setCompanies(transformCompanies(companiesResponse.data.results))
      } catch (error) {
        console.error("Failed to fetch data", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleEmailChange = async e => {
    const value = e.target.value.toLowerCase()
    setValue("personal_email", value)

    // Clear the duplicate email error
    setIsDuplicateEmail(false)
    setDuplicateCandidateId(null)

    // Trigger validation for the email field
    await trigger("personal_email")

    if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current)
    emailDebounceRef.current = setTimeout(async () => {
      if (value) {
        try {
          const response = await axios.post(`/api/v1/candidates/check-email/?company_id=${companyId}`, {
            email: value,
          })
          if (response.data.duplicate) {
            setIsDuplicateEmail(true)
            setDuplicateCandidateId(response.data.candidate_id)
          } else {
            setIsDuplicateEmail(false)
            setDuplicateCandidateId(null)
          }
        } catch (error) {
          console.error("Failed to check duplicate email", error)
        }
      }
    }, 700)
  }

  // Handle country change
  const handleCountryChange = async country => {
    setValue("country", country)
    if (country?.country_id) {
      try {
        const response = await axios.get(
          `/api/v1/get-all-states/?country_id=${country.country_id}`
        )
        setStates(transformStates(response.data.results))
      } catch (error) {
        console.error("Failed to fetch states", error)
      }
    }
  }
  const [resumeFile, setResumeFile] = useState(null)
  const [resumeError, setResumeError] = useState("")
  const handleResumeFileChange = e => {
    const file = e.target.files[0]
    if (!file) return

    // Allowed file types
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/png",
    ]

    // Max file size: 5MB
    const maxSize = 5 * 1024 * 1024

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      setResumeError(
        "Invalid file type. Only PDF, DOC, DOCX, JPEG, or PNG are allowed."
      )
      return
    }

    // Validate file size
    if (file.size > maxSize) {
      setResumeError("File size must be less than 5MB.")
      return
    }

    // Convert file to base64
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      setResumeFile({
        file_name: file.name,
        file_data: reader.result.split(",")[1], // Remove the data URL prefix
        type: file.type,
        size: file.size,
      })
      setResumeError("")
    }
    reader.onerror = () => {
      setResumeError("Failed to process file.")
    }
  }

  // Handle state change
  const handleStateChange = async state => {
    setValue("state", state)
    if (state?.state_id) {
      try {
        const response = await axios.get(
          `/api/v1/get-all-cities/?state_id=${state.state_id}`
        )
        setCities(transformCities(response.data.results))
      } catch (error) {
        console.error("Failed to fetch cities", error)
      }
    }
  }
  const [primarySkillsview, setPrimarySkillsview] = useState([])
  const [secondarySkillsview, setSecondarySkillsview] = useState([]);
  const [otherSkillsview, setOtherSkillsview] = useState([]);
  const [formData, setFormData] = useState({
    street_address: "",
    street_address_line_2: "",
    country: null,
    state: null,
    city: null,
    pincode: "",
    label: null,
    source: null,
    stage: null,
    jobtitle: "",
    Company: "",
    availabilityDate: "",
    userid: "",
  })

  const [resumefileData, setResumeFileData] = useState({})
  const [employeeTypeOptions, setEmployeeTypeOptions] = useState([])
  const fetchAddressLabels = async () => {
    try {
      const response = await axios.get("/api/v1/get-all-address-label/")
      if (response?.data?.results) {
        const labels = response.data.results.map(label => ({
          label: label.address_label, // e.g., "Current Address"
          value: label.id, // Use the ID as the value
        }))
        setAddressLabels(labels)
      }
    } catch (error) {
      console.error("Error fetching address labels:", error)
    }
  }

  const getEmployeeTypes = async () => {
    try {
      const response = await axios.get("/api/v1/get-all-employee-type/")
      if (response.data?.results) {
        setEmployeeTypeOptions(
          response.data.results.map(item => ({
            label: item.employee_type,
            value: item.id,
          }))
        )
      }
    } catch (error) {
      console.error("Error fetching employee types:", error)
    }
  }

  const getDocumentTypes = async () => {
    try {
      const response = await axios.get("/api/v1/get-all-document-type/")
      if (response.data?.results) {
        const transformedData = response.data.results.map(doc => ({
          label: doc.value,
          value: doc.id, // Use unique ID for value
        }))
        setDocumentTypes(transformedData)
      }
    } catch (error) {
      console.error("Error fetching document types:", error)
      triggerAlert("error", "Error", "Failed to fetch document types.")
    }
  }
  const handleCandidateFileChange = async event => {
    const file = event.target.files[0]
    if (!file) return

    // File size validation (5MB limit)
    const maxSize = 5 * 1024 * 1024 // 5MB in bytes
    if (file.size > maxSize) {
      setResumeError("File size must be less than 5MB")
      event.target.value = ""
      return
    }

    // File type validation - match AllActiveemp exactly
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/png",
    ]

    if (!allowedTypes.includes(file.type)) {
      setResumeError(
        "Invalid file type. Please upload PDF, DOC, DOCX, JPEG, or PNG files only."
      )
      event.target.value = ""
      return
    }

    // Prevent folder uploads
    if (file.size === 0 || !file.type) {
      setResumeError("Folders are not allowed. Please upload a valid file.")
      event.target.value = ""
      return
    }

    try {
      const base64 = await getCandidateBase64(file)
      // Match AllActiveemp structure exactly
      setResumeFileData({
        file_name: file.name,
        file_data: base64.split(",")[1], // Remove data:mime;base64, prefix like in AllActiveemp
        type: file.type,
        size: file.size,
      })
      setResumeError("")
      setCandidateError("") // Clear general error
    } catch (error) {
      setResumeError("Failed to process file")
      setCandidateError("Failed to process file")
      event.target.value = ""
    }
  }

  const onSubmit = async data => {
    setLoading(true)
    try {
      const payload = {
        first_name: data.firstname,
        last_name: data.lastname,
        email: data.personal_email,
        mobile_phone: data.phone,
        job_title: data.jobtitle,
        current_company: data.Company,
        primary_skills: primarySkillsview,
        secondary_skills: secondarySkillsview, 
        other_skills: otherSkillsview, 
        relocation: data.relocation || false,
        private: data.private || false,
        resume: resumeFile,
        street_address: formData.street_address,
        street_address_line_2: formData.street_address_line_2,
        country: formData.country?.name,
        state: formData.state?.name,
        city: formData.city?.name,
        pincode: formData.pincode,
        address_label: formData.label?.label,
        source: formData.source, // Use the `value` property from the selected source
        stage: "received",
        current_salary: data.current_salary, 
        expected_salary: data.expected_salary, 
        referred_by: data.referred_by,
      }

      await axios.post(`/api/v1/candidates/?company_id=${companyId}`,payload )

      Swal.fire({
        title: "Success!",
        text: "Candidate created successfully!",
        icon: "success",
        confirmButtonText: "OK",
      })
      reset()
      navigate("/all_candidates")
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to create candidate. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      })
    } finally {
      setLoading(false)
    }
  }

  const [companies, setCompanies] = useState([])
  const [selectedCategoryNode, setSelectedCategoryNode] = useState(null)
  const [categoryNodes, setCategoryNodes] = useState([])
  const transformCompanies = companies =>
    companies.map(company => ({
      name: company.company_name,
      id: company.company_id,
    }))
  const geteditvaluescondidates = async id => {
    try {
      const response = await axios.get(`/api/v1/candidates/${id}/`)
      if (response.status === 200) {
        const results = response.data

        // Parse dates
        const parsedData = {
          ...results,
          availability_date: results.availability_date
            ? new Date(results.availability_date)
            : null,
          dob: results.dob ? new Date(results.dob) : null,
          created_at: results.created_at
            ? new Date(results.created_at)
            : new Date(),
          updated_at: results.updated_at
            ? new Date(results.updated_at)
            : new Date(),
          last_activity_date: results.last_activity_date
            ? new Date(results.last_activity_date)
            : null,
          notes: results.candidate_notes || "",
        }

        // Set form values except skills
        Object.keys(parsedData).forEach(key => {
          if (
            !["primary_skills", "secondary_skills", "other_skills"].includes(
              key
            )
          ) {
            setValue(key, parsedData[key])
          }
        })

        // Set resume if it exists
        if (results.resume) {
          const fileName = results.resume.split("/").pop()
          const mimeType = getMimeType(results.resume)
          setFileData({
            name: fileName,
            type: mimeType,
            base64: results.resume,
          })
        } else {
          setFileData(null)
        }

        // Set address-related fields
        setStreet1(results.street_address || "")
        setStreet2(results.street_address_line_2 || "")
        setPostalCode(results.pincode || "")
        setSelectedCategoryNode(results.categories || null)
        setSelectedGroupNode(results.groups || null)
        setCandidateCountryId(results.country || null)
        setCandidateStateId(results.state || null)
        setCandidateCityId(results.city || null)

        // Set address label
        if (results.address_label) {
          setCandidateAddressLabel(results.address_label)
        } else {
          setCandidateAddressLabel(null)
        }

        // Set country, state, city
        if (results.country) {
          const selectedCountryObj = addCountries.find(
            item => item.id === results.country
          )
          setSelectedCountry(selectedCountryObj || null)
        }
        if (results.state) {
          const selectedStateObj = addStates.find(
            item => item.id === results.state
          )
          setSelectedState(selectedStateObj || null)
        }
        if (results.city) {
          const selectedCityObj = addCities.find(
            item => item.id === results.city
          )
          setSelectedCity(selectedCityObj || null)
        }

        // Update address field
        setAddress(
          [
            results.street_address,
            results.street_address_line_2,
            results.city_name,
            results.state_name,
            results.country_name,
            results.pincode,
            results.address_label, // Include address_label directly from API
          ]
            .filter(Boolean)
            .join(", ")
        )

        setPrimarySkillsview(results.primary_skills || null)

        // Set employee type
        const employeeTypeOption = employeeTypeOptions.find(
          opt => opt.label === results.employee_type
        )
        setValue(
          "employee_type",
          employeeTypeOption ? employeeTypeOption.value : ""
        )
      }
    } catch (error) {
      console.error("Error fetching candidate:", error)
    }
  }

  const getResumeSources = async () => {
    try {
      const response = await axios.get(`/api/v1/resume-source/`)
      const transformedData = response.data.results.map(workv => ({
        label: workv.resume_source,
        value: workv.source_id,
      }))
      setSourceOptions(transformedData)
    } catch (error) {
      console.error("Error fetching resume sources:", error)
    }
  }

  const getSkills = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-skills/`)
      const transformedData = response.data.results.map(workv => ({
        id: workv.skill_id,
        name: workv.skill_name,
      }))
      setPrimarySkillsget(transformedData)
    } catch (error) {
      console.error("Error fetching skills:", error)
    }
  }
  const getCategoriesitem = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-categories/`)
      if (response.data?.results) {
        const transformedData = response.data.results.map(category => ({
          name: category.name,
          id: category.category_id,
        }))
        setCategoriesitem(transformedData)
      }
    } catch (error) {
      console.error("Error fetching categories:", error)
    }
  }

  const getGroupsitemsub = async category_ids => {
    try {
      const response = await axios.get(
        `/api/v1/get-all-groups/?category_id=${category_ids}`
      )
      if (response.data?.results) {
        const transformedDatagroup = response.data.results.map(group => ({
          name: group.name,
          id: group.group_id,
        }))
        setGroupitem(transformedDatagroup)
      }
    } catch (error) {
      console.error("Error fetching groups:", error)
    }
  }

  const getAddCountries = async () => {
    try {
      const response = await axios.get(`/api/v1/get-all-countries/`)
      const transformedData = response.data.results.map(country => ({
        name: country.name,
        id: country.country_id,
      }))
      setAddCountries(transformedData)
    } catch (error) {
      console.error("Error fetching countries:", error)
    }
  }

  const getAddStates = async countryId => {
    try {
      const response = await axios.get(
        `/api/v1/get-all-states/?country_id=${countryId}`
      )
      const transformedData = response.data.results.map(state => ({
        name: state.name,
        id: state.state_id,
      }))
      setAddStates(transformedData)
    } catch (error) {
      console.error("Error fetching states:", error)
    }
  }

  const getAddCities = async stateId => {
    try {
      const response = await axios.get(
        `/api/v1/get-all-cities/?state_id=${stateId}`
      )
      const transformedData = response.data.results.map(city => ({
        name: city.name,
        id: city.city_id,
      }))
      setAddCities(transformedData)
    } catch (error) {
      console.error("Error fetching cities:", error)
    }
  }
  const getCandidateBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = error => reject(error)
    })
  }
  const [groupNodes, setGroupNodes] = useState([])
  const [selectedGroupNode, setSelectedGroupNode] = useState(null)
  return (
    <React.Fragment>
      <div className="page-content allact-tabs manager-dash">
        <Container fluid>
          <div className="page-title-box mb-0 recruiter-dashboard actjobsum">
            <Card className="shadow-md border-0">
              <h1 className="page-title">Create a Candidate</h1>
              <Row className="align-items-center">
                <Col xxl={12}>
                  <div className="">
                    {loading ? (
                      <div className="text-center p-5">
                        <ProgressSpinner />
                      </div>
                    ) : (
                      <>
                        <Steps
                          model={steps}
                          activeIndex={activeIndex}
                          readOnly={false}
                          className="mb-4"
                        />
                        <form onSubmit={handleSubmit(onSubmit)}>
                          {activeIndex === 0 && (
                            <div>
                              <h3>Basic Details</h3>
                              <Row className="mb-3">
                                <Col lg={6}>
                                  <label className="mb-0">
                                    First Name{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <InputText
                                    {...register("firstname", {
                                      required: "First name is required",
                                      pattern: {
                                        value: /^[A-Za-z][A-Za-z-\s&,]+$/,
                                        message:
                                          "Only alphabets and spaces are allowed",
                                      },
                                    })}
                                    onKeyUp={() => trigger("firstname")}
                                    className="form-control"
                                  />
                                  {errors.firstname && (
                                    <small className="text-danger">
                                      {errors.firstname.message}
                                    </small>
                                  )}
                                </Col>
                                <Col lg={6}>
                                  <label className="mb-0">Last Name</label>
                                  <InputText
                                    {...register("lastname", {
                                      pattern: {
                                        value: /^[A-Za-z][A-Za-z-\s&]+$/,
                                        message:
                                          "Only alphabets and spaces are allowed",
                                      },
                                    })}
                                    onKeyUp={() => trigger("lastname")}
                                    className="form-control"
                                  />
                                  {errors.lastname && (
                                    <small className="text-danger">
                                      {errors.lastname.message}
                                    </small>
                                  )}
                                </Col>
                              </Row>
                              <Row className="mb-3">
                                <Col lg={6}>
                                  <label className="mb-0">
                                    Email <span className="text-danger">*</span>
                                  </label>
                                  <InputText
                                    {...register("personal_email", {
                                      required: "Email is required",
                                      pattern: {
                                        value:
                                          /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                        message: "Invalid email address",
                                      },
                                    })}
                                    onChange={handleEmailChange}
                                    className="form-control"
                                  />
                                  {isDuplicateEmail && (
                                    <small className="text-danger">
                                      Candidate with this email already exists.
                                    </small>
                                  )}
                                  {errors.personal_email && (
                                    <small className="text-danger">
                                      {errors.personal_email.message}
                                    </small>
                                  )}
                                </Col>
                                <Col lg={6}>
                                  <label className="mb-0">
                                    Phone <span className="text-danger">*</span>
                                  </label>
                                  <InputText
                                    {...register("phone", {
                                      required: "Phone is required",
                                      pattern: {
                                        value: /^[0-9]+$/,
                                        message: "Only numbers are allowed",
                                      }, 
                                      minLength: {
                                        value: 10,
                                        message: "Phone number must be 10 digits",
                                      },
                                      maxLength: {
                                        value: 10,
                                        message: "Phone number must be 10 digits",
                                      },
                                    })}
                                    onKeyUp={() => trigger("phone")}
                                    className="form-control"
                                  />
                                  {errors.phone && (
                                    <small className="text-danger">
                                      {errors.phone.message}
                                    </small>
                                  )}
                                </Col>
                              </Row>
                              <Row className="mb-3 mt-2">
                                <Col lg={6}>
                                  <label className="mb-0">Job Title</label>
                                  <InputText
                                    {...register("jobtitle")}
                                    className="form-control"
                                  />
                                </Col>
                                <Col lg={6}>
                                  <label className="mb-0">Previous Organization</label>
                                   <InputText
                                    {...register("Company")}
                                    className="form-control"
                                  />
                                </Col>
                              </Row>
                            </div>
                          )}

                          {activeIndex === 1 && (
                            <div>
                              <h3>Technical Skills</h3>
                              <Row className="mb-3">
                                <Col lg={6}>
                                  <label className="mb-0">
                                    Source{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <Controller
                                    name="source"
                                    control={control}
                                    rules={{ required: "Source is required" }}
                                    render={({ field }) => (
                                      <Dropdown
                                        value={field.value}
                                        options={resumeSources}
                                        placeholder="Select Source"
                                        className="w-100"
                                        onChange={e => {
                                          field.onChange(e.value)
                                          setFormData({
                                            ...formData,
                                            source: e.value,
                                          })
                                          clearErrors("source");
                                        }}
                                        optionLabel="label"
                                        optionValue="value"
                                      />
                                    )}
                                  />
                                  {errors.source && (
                                    <small className="text-danger">
                                      {errors.source.message}
                                    </small>
                                  )}
                                </Col>

                                <Col lg={6}>
                                  <label className="mb-0">Primary Skills</label>
                                  <Controller
                                    name="primarySkills"
                                    control={control}
                                    render={({ field }) => (
                                      <MultiSelect
                                        value={field.value}
                                        options={primarySkills}
                                        optionLabel="name"
                                        optionValue="id"
                                        filter
                                        filterBy="name"
                                        resetFilterOnHide
                                        virtualScrollerOptions={{ itemSize: 40 }}
                                        placeholder="Select primary skills..."
                                        className="w-100"
                                        onChange={e => {
                                          field.onChange(e.value)
                                          setPrimarySkillsview(e.value)
                                        }}
                                      />
                                    )}
                                  />
                                </Col>

                                <Col lg={6} className="mt-2">
                                  <label className="mb-0">
                                    Secondary Skills
                                  </label>
                                  <Controller
                                    name="secondarySkills"
                                    control={control}
                                    render={({ field }) => (
                                      <MultiSelect
                                        value={field.value}
                                        options={primarySkills}
                                        optionLabel="name"
                                        optionValue="id"
                                        filter
                                        filterBy="name"
                                        resetFilterOnHide
                                        virtualScrollerOptions={{ itemSize: 40 }}
                                        placeholder="Select  secondary skills..."
                                        className="w-100"
                                        onChange={e => {
                                          field.onChange(e.value)
                                          setSecondarySkillsview(e.value);
                                        }}
                                      />
                                    )}
                                  />
                                </Col>
                                <Col lg={6} className="mt-2">
                                  <label className="mb-0">Other Skills</label>
                                  <Controller
                                    name="otherSkills"
                                    control={control}
                                    render={({ field }) => (
                                      <MultiSelect
                                        value={field.value}
                                        options={primarySkills}
                                        optionLabel="name"
                                        optionValue="id"
                                        filter
                                        filterBy="name"
                                        resetFilterOnHide
                                        virtualScrollerOptions={{ itemSize: 40 }}
                                        placeholder="Select Other skills..."
                                        className="w-100"
                                        onChange={e => {
                                          field.onChange(e.value)
                                          setOtherSkillsview(e.value);
                                        }}
                                      />
                                    )}
                                  />
                                </Col>
                                <Col lg={6} className="mt-2">
                                  <label className="mb-0">
                                    Availability Date
                                  </label>
                                  <InputText
                                    type="date"
                                    {...register("availabilityDate")}
                                    className="form-control"
                                  />
                                </Col>
                              </Row>
                            </div>
                          )}

                          {activeIndex === 2 && (
                            <div>
                              <h3>Import Resume</h3>
                              <Row className="mb-3">
                                <Col lg={12}>
                                  <label className="mb-0">
                                    Resume Attachment{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  {/* <InputText
                                    type="file"
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                    onChange={handleCandidateFileChange}
                                    className="form-control"
                                  /> */}
                                  <Controller
                                    name="resumeFile"
                                    control={control}
                                    rules={{ required: "Resume is required" }}
                                    render={({ field }) => (
                                      <InputText
                                        type="file"
                                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                        onChange={e => {
                                          handleResumeFileChange(e);
                                          field.onChange(e.target.files[0]);
                                        }}
                                        className="form-control"
                                      />
                                    )}
                                  />
                                  {resumeError && (
                                    <small className="text-danger">
                                      {resumeError}
                                    </small>
                                  )}
                                  <small className="text-muted">
                                    Eg: (jpeg, png, pdf, jpg, doc, docx) upto
                                    5MB
                                  </small>
                                  {resumeFile && (
                                    <div className="mt-2">
                                      <small className="text-success">
                                        {resumeFile.file_name} (
                                        {(
                                          resumeFile.size /
                                          1024 /
                                          1024
                                        ).toFixed(2)}{" "}
                                        MB)
                                      </small>
                                    </div>
                                  )}
                                </Col>
                              </Row>
                            </div>
                          )}

                          {activeIndex === 3 && (
                            <div>
                              <h3 className="mb-2">Contact Details</h3>
                              <Row className="mb-3">
                                <Col lg={6}>
                                  <label className="mb-0">Street 1</label>
                                  <InputText
                                    value={formData.street_address}
                                    onChange={e =>
                                      setFormData({
                                        ...formData,
                                        street_address: e.target.value,
                                      })
                                    }
                                    className="form-control"
                                  />
                                </Col>
                                <Col lg={6}>
                                  <label className="mb-0">Street 2</label>
                                  <InputText
                                    value={formData.street_address_line_2}
                                    onChange={e =>
                                      setFormData({
                                        ...formData,
                                        street_address_line_2: e.target.value,
                                      })
                                    }
                                    className="form-control"
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-3">
                                <Col lg={6}>
                                  <label className="mb-0">Country</label>
                                  <Controller
                                    name="country"
                                    control={control}
                                    render={({ field }) => (
                                      <Dropdown
                                        value={field.value}
                                        options={countries}
                                        optionLabel="name"
                                        placeholder="Select Country"
                                        className="w-100"
                                        onChange={e => {
                                          field.onChange(e.value)
                                          handleCountryChange(e.value)
                                        }}
                                      />
                                    )}
                                  />
                                </Col>
                                <Col lg={6}>
                                  <label className="mb-0">State</label>
                                  <Controller
                                    name="state"
                                    control={control}
                                    render={({ field }) => (
                                      <Dropdown
                                        value={field.value}
                                        options={states}
                                        optionLabel="name"
                                        placeholder="Select State"
                                        className="w-100"
                                        onChange={e => {
                                          field.onChange(e.value)
                                          handleStateChange(e.value)
                                        }}
                                      />
                                    )}
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-3">
                                <Col lg={6}>
                                  <label className="mb-0">City</label>
                                  <Controller
                                    name="city"
                                    control={control}
                                    render={({ field }) => (
                                      <Dropdown
                                        value={field.value}
                                        options={cities}
                                        optionLabel="name"
                                        placeholder="Select City"
                                        className="w-100"
                                        onChange={e => field.onChange(e.value)}
                                      />
                                    )}
                                  />
                                </Col>
                                <Col lg={6}>
                                  <label className="mb-0">Postal Code</label>
                                  <InputText
                                    value={formData.pincode}
                                    maxLength={10}
                                    onChange={e =>
                                      setFormData({
                                        ...formData,
                                        pincode: e.target.value,
                                      })
                                    }
                                    className="form-control"
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-3">
                                <Col lg={6} className="mt-2">
                                  <label className="mb-0">Label</label>
                                  <Controller
                                    name="label"
                                    control={control}
                                    render={({ field }) => (
                                      <Dropdown
                                        value={field.value}
                                        options={addressLabels}
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder="Select Label"
                                        className="w-100"
                                        onChange={e => field.onChange(e.value)}
                                      />
                                    )}
                                  />
                                </Col>
                              </Row>
                            </div>
                          )}

                          {activeIndex === 4 && (
                            <div>
                              <h3>Other Details</h3>
                              <Row className="mb-3">
                                <Col lg={6}>
                                  <label className="mb-0">Current Salary</label>
                                  <Controller
                                    name="current_salary"
                                    control={control}
                                    render={({ field }) => (
                                      <InputNumber
                                        inputId="current_salary"
                                        mode="currency"
                                        currency="INR"
                                        locale="en-IN"
                                        placeholder="Enter current annual salary"
                                        className="w-full"
                                        value={field.value ?? 0.0} // Default to 0.00 if null/undefined
                                        onValueChange={e =>
                                          field.onChange(e.value ?? 0.0)
                                        } // Ensure 0.00 on empty input
                                        useGrouping
                                        min={0}
                                      />
                                    )}
                                  />
                                </Col>
                                <Col lg={6}>
                                  <label className="mb-0">
                                    Expected Salary
                                  </label>
                                  <Controller
                                    name="expected_salary"
                                    control={control}
                                    render={({ field }) => (
                                      <InputNumber
                                        inputId="expected_salary"
                                        mode="currency"
                                        currency="INR"
                                        locale="en-IN"
                                        placeholder="Enter expected annual salary"
                                        className="w-full"
                                        value={field.value ?? 0.0}
                                       onValueChange={(e) => {
                                          field.onChange(e.value ?? 0.0);
                                          trigger("expected_salary"); 
                                        }}
                                        useGrouping
                                        min={0}
                                      />
                                    )}
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-3">
                                <Col lg={6}>
                                  <label className="mb-0">Employee Type</label>
                                  <Controller
                                    name="employee_type"
                                    control={control}
                                    render={({ field }) => (
                                      <Dropdown
                                        {...field}
                                        value={field.value}
                                        options={employeeTypeOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        placeholder="Select Employee Type"
                                        className="w-full border border-gray-400 rounded-md bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                        onChange={e => field.onChange(e.value)}
                                      />
                                    )}
                                  />
                                </Col>
                                <Col lg={6}>
                                  <label className="mb-0">Date of Birth</label>
                                  <Controller
                                    name="dob"
                                    control={control}
                                    render={({ field }) => {
                                      const today = new Date()
                                      const yesterday = new Date(today)
                                      yesterday.setDate(today.getDate() - 1)

                                      return (
                                        <Calendar
                                          value={field.value}
                                          onChange={e => {
                                            field.onChange(e.value)
                                            trigger("dob") // Trigger validation on change
                                          }}
                                          dateFormat="yy-mm-dd"
                                          showIcon
                                          className="w-full"
                                          icon="pi pi-calendar"
                                          maxDate={yesterday} // <-- Only allow dates before today
                                        />
                                      )
                                    }}
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-3">
                                {/* <Col lg={6}>
                                  <label className="block mb-2">
                                    Referred By
                                  </label>
                                  <InputText
                                    className="w-full"
                                    placeholder="Enter referrer"
                                    {...register("referred_by")}
                                  />
                                </Col> */}
                              <Col lg={6}>
                                <label className="block mb-2">Referred By</label>
                                <Controller
                                  name="referred_by"
                                  control={control} // from useForm()
                                  rules={{ required: "Referred By is required" }}
                                  render={({ field, fieldState }) => (
                                    <>
                                      <Dropdown
                                        className={`w-full ${fieldState.error ? "p-invalid" : ""}`}
                                        placeholder="Select referrer"
                                        options={options} // [{label, value}]
                                        value={field.value} // controlled value from react-hook-form
                                        onChange={(e) => field.onChange(e.value)} // update form value
                                      />
                                      {fieldState.error && (
                                        <small className="text-danger">{fieldState.error.message}</small>
                                      )}
                                    </>
                                  )}
                                />
                              </Col>

                                <Col lg={6}>
                                  <label className="block mb-2">
                                    Categories
                                  </label>
                                  <TreeSelect
                                    value={selectedCategoryNode}
                                    options={categoryNodes}
                                    onChange={e =>
                                      setSelectedCategoryNode(e.value)
                                    }
                                    className="w-full"
                                    placeholder="Select Category"
                                    filter
                                  />
                                </Col>
                              </Row>
                              <Row className="mb-3">
                                <Col lg={6}>
                                  <label className="mb-0">Groups</label>
                                  <TreeSelect
                                    value={selectedGroupNode}
                                    options={groupNodes}
                                    onChange={e =>
                                      setSelectedGroupNode(e.value)
                                    }
                                    className="w-full"
                                    placeholder="Select Group"
                                    filter
                                  />
                                </Col>
                              </Row>                     
                            </div>
                            
                          )}

                          <Row>
                            <Col>
                              <div className="d-flex justify-content-between">
                                <Button
                                  type="button"
                                  label="Previous"
                                  icon="pi pi-arrow-left"
                                  onClick={() => setActiveIndex(activeIndex - 1)}
                                  disabled={activeIndex === 0}
                                  className="p-button-text blueBtn"
                                >
                                  Previous
                                </Button>
                                
                              </div>
                            </Col>
                            <Col>
                              {activeIndex < steps.length - 1 && (
                                <div className="d-flex justify-content-end">
                                    <Button
                                      type="button"
                                      label="Next"
                                      icon="pi pi-arrow-right"
                                      onClick={() => handleTabChange(activeIndex + 1)}
                                      className="p-button-text blueBtn"
                                    >
                                      Next
                                    </Button>
                                  </div>
                                )}
                              {activeIndex === 4 && (
                                <div className="d-flex justify-content-end">
                                    <Button
                                      type="submit"
                                      label="Create"
                                      icon="pi pi-check"
                                      className="p-button-success blueBtn"
                                      disabled={loading}
                                    >
                                      {loading ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                          Creating...
                                        </>
                                      ) : (
                                        "Create"
                                      )}
                                    </Button>
                                </div>
                              )}
                            </Col>
                          </Row>

                          {/* Navigation Buttons */}
                          

                        </form>
                      </>
                    )}
                  </div>
                </Col>
              </Row>
            </Card>
          </div>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default CandidateStepperForm
